import { useRef, useState, useCallback, useEffect } from "react"
import { Loader2 } from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"

const THRESHOLD = 64
const MAX_PULL = 120
const INDICATOR_SIZE = 48

export function PullToRefresh({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const containerRef = useRef<HTMLDivElement>(null)
  const startY = useRef(0)
  const pulling = useRef(false)
  const [pullDistance, setPullDistance] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  // Track whether we're actively pulling (no transition) vs releasing (with transition)
  const [releasing, setReleasing] = useState(false)

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (refreshing) return
      if (window.scrollY > 0) return
      startY.current = e.touches[0].clientY
      pulling.current = true
      setReleasing(false)
    },
    [refreshing]
  )

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!pulling.current || refreshing) return
      const delta = e.touches[0].clientY - startY.current
      if (delta < 0) {
        pulling.current = false
        setPullDistance(0)
        return
      }
      // Rubber-band damping: stronger resistance as you pull further
      const dampened = MAX_PULL * (1 - Math.exp(-delta / 200))
      setPullDistance(dampened)
    },
    [refreshing]
  )

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current && pullDistance === 0) return
    pulling.current = false
    setReleasing(true)

    if (pullDistance >= THRESHOLD) {
      setRefreshing(true)
      setPullDistance(INDICATOR_SIZE)
      await queryClient.invalidateQueries()
      setRefreshing(false)
    }
    setPullDistance(0)
    // Clear releasing after transition so transform is fully removed from DOM
    setTimeout(() => setReleasing(false), 300)
  }, [pullDistance, queryClient])

  useEffect(() => {
    const node = containerRef.current
    if (!node) return
    node.addEventListener("touchstart", handleTouchStart, { passive: true })
    node.addEventListener("touchmove", handleTouchMove, { passive: true })
    node.addEventListener("touchend", handleTouchEnd)
    return () => {
      node.removeEventListener("touchstart", handleTouchStart)
      node.removeEventListener("touchmove", handleTouchMove)
      node.removeEventListener("touchend", handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd])

  const progress = Math.min(pullDistance / THRESHOLD, 1)
  const useTransition = releasing || refreshing
  // Only apply transform when actively pulling/releasing/refreshing.
  // transform (even translateY(0)) creates a containing block that breaks sticky
  // and a GPU layer that causes sub-pixel jitter on scroll.
  const isAnimating = pullDistance > 0 || useTransition

  return (
    <div ref={containerRef} className="relative overflow-clip">
      {/* Indicator: positioned above content, revealed by translateY */}
      <div
        className="absolute left-0 right-0 flex items-center justify-center"
        style={{
          top: -INDICATOR_SIZE,
          height: INDICATOR_SIZE,
          ...(isAnimating && {
            transform: `translateY(${pullDistance}px)`,
            transition: useTransition ? "transform 0.3s cubic-bezier(0.2, 0, 0, 1)" : "none",
          }),
        }}
      >
        <div
          className={refreshing ? "animate-spin" : ""}
          style={
            refreshing
              ? undefined
              : { transform: `rotate(${progress * 360}deg)`, opacity: progress }
          }
        >
          <Loader2 className="size-5 text-muted-foreground" />
        </div>
      </div>

      {/* Content: shifts down with translateY */}
      <div
        style={isAnimating ? {
          transform: `translateY(${pullDistance}px)`,
          transition: useTransition ? "transform 0.3s cubic-bezier(0.2, 0, 0, 1)" : "none",
          willChange: pulling.current ? "transform" : "auto",
        } : undefined}
      >
        {children}
      </div>
    </div>
  )
}
